import type { CSSProperties } from 'react';
import { useWebshopContext } from '../state/WebshopContext';
import { substituteContentBlocks } from '../parse/contentBlocks';

interface HtmlContentProps {
    /** Admin-authored HTML — info/title/static body. May contain
     * `$content[identifier]` placeholders which are resolved against
     * the host plugin's CMS-block map (via preview context). */
    html: string;
    className?: string;
    style?: CSSProperties;
    tag?: 'div' | 'span';
}

/**
 * Renders trusted admin-authored HTML and expands `$content[identifier]`
 * placeholders against the host plugin's CMS-block map. Use this at
 * every `dangerouslySetInnerHTML` site so the substitution is uniform —
 * Characteristic info, Value info popovers, Static type bodies, Toggle
 * value info, …
 *
 * The substitution happens at render time (cheap regex over a short
 * string) rather than at parse time so block-map updates take effect on
 * the next render without a Component re-parse.
 *
 * Trust model: same as legacy — admin users author the HTML in the same
 * security context as the bundle itself, so eval-style content (custom
 * CSS / JS inside info HTML) is the explicit feature, not a third-party
 * injection concern.
 */
export function HtmlContent({ html, className, style, tag = 'div' }: HtmlContentProps) {
    const preview = useWebshopContext();
    const resolved = substituteContentBlocks(html, preview?.cmsBlocks);
    const Tag = tag;
    return (
        <Tag
            className={className}
            style={style}
            dangerouslySetInnerHTML={{ __html: resolved }}
        />
    );
}
